import { useCallback, useMemo, useState } from 'react';
import {
    AnswerList,
    AnswerRecord,
    PrefillData,
    PrefillQuestion,
    PrefillSection,
    QUIZ_FLOW_TYPES,
    QUIZ_QUESTION_TYPES,
    QuestionList,
    QuestionRecord,
    QuizFlow,
    QuizRecord,
} from 'src/types/Quiz';

// Remap API records to be easily consumable
const modifyAnswerList = (answerList: AnswerRecord[]): AnswerList =>
    Array.isArray(answerList)
        ? answerList.map((a) => ({
              ...a,
              answerId: a.answerFrontendStamp
                  ? a.answerFrontendStamp
                  : a.frontendStamp
                    ? a.frontendStamp
                    : '',
          }))
        : [];

const modifyQuestionList = (questionList: QuestionRecord[]): QuestionList =>
    Array.isArray(questionList)
        ? questionList.map((q) => ({
              ...q,
              questionId: q.frontendStamp || '',
              answerList: modifyAnswerList(q.answerList),
              prefillAnswers: modifyAnswerList(q.prefillAnswers),
              userAnswers: modifyAnswerList(q.userAnswers),
          }))
        : [];

type QuizHookProps = {
    quizFlow: QuizFlow;
    quizResponse?: QuizRecord | null;
};

const useQuiz = ({ quizFlow, quizResponse = null }: QuizHookProps) => {
    const [submittedQuestionIds, setSubmittedQuestionIds] = useState<string[]>(
        [],
    );
    // Generate question list
    const generateQuestionList = useCallback(
        (quizResponse: QuizRecord | null) => {
            return quizResponse !== null &&
                Array.isArray(quizResponse?.questionList)
                ? modifyQuestionList(quizResponse.questionList)
                : [];
        },
        [],
    );

    const questionList = useMemo(
        () => generateQuestionList(quizResponse),
        [quizResponse, generateQuestionList],
    );

    // Generate dictionary to get question by id
    const questionById = useMemo(
        () =>
            quizResponse !== null && Array.isArray(questionList)
                ? Object.fromEntries(
                      questionList.map((q) => [q.frontendStamp, q]),
                  )
                : {},
        [quizResponse, questionList],
    );
    // Generate dictionary to get section by id
    const sectionById = useMemo(
        () =>
            quizResponse !== null && Array.isArray(quizResponse?.sectionList)
                ? Object.fromEntries(
                      quizResponse.sectionList.map((s) => [
                          s.sectionId,
                          s.sectionName,
                      ]),
                  )
                : {},
        [quizResponse],
    );

    // Generates prefill data
    const generatePrefillData = useCallback(
        (questionList: QuestionRecord[]): PrefillData => {
            // Find all prefill parents
            const prefillParentIds = questionList
                .filter((q) => q.questionType === QUIZ_QUESTION_TYPES.prefilled)
                .map((q) => q.questionId);
            if (prefillParentIds.length > 0) {
                const newPrefillById = {};
                let newPrefillQuestionIds = [] as string[];
                // Generate prefill for each parent
                prefillParentIds.forEach((questionId) => {
                    if (questionId) {
                        const prefillQuestion = {
                            questionId: questionId,
                            questionList: [],
                            sectionById: {},
                        } as PrefillQuestion;

                        // Get ids of all parent questions assigned to prefill parent
                        const parentIds = questionList
                            .filter(
                                (q) =>
                                    q.prefillNeeded &&
                                    q.parentQuestion === questionId,
                            )
                            .map((q) => q.questionId);
                        // Get ids of all child questions assigned to prefill parent
                        const childIds = questionList
                            .filter((q) =>
                                q.parentQuestion
                                    ? parentIds.includes(q.parentQuestion)
                                    : false,
                            )
                            .map((q) => q.questionId);
                        // Get question list from these ids
                        const prefillQuestionList = questionList.filter((q) =>
                            [...parentIds, ...childIds].includes(q.questionId),
                        );
                        // Build a list of question lists paired to their section ID
                        // Allows questions to be displayed in order, grouped by section
                        const prefillQuestionSectionById =
                            prefillQuestionList.reduce(
                                (sectionById, currentQuestion) => {
                                    const newSectionById = sectionById;
                                    const { sectionId } = currentQuestion;
                                    if (sectionId) {
                                        // Check if section exists
                                        if (
                                            Object.keys(sectionById).includes(
                                                sectionId.toString(),
                                            )
                                        ) {
                                            // Add question to existing record
                                            newSectionById[
                                                sectionId
                                            ].questionList.push(
                                                currentQuestion,
                                            );
                                        } else {
                                            // Create new record
                                            const prefillSection = {
                                                sectionId: sectionId,
                                                questionList: [currentQuestion],
                                            } as PrefillSection;
                                            newSectionById[sectionId] =
                                                prefillSection;
                                        }
                                    }

                                    return newSectionById;
                                },
                                {},
                            );

                        // Update prefill question attributes
                        prefillQuestion.questionList = prefillQuestionList;
                        prefillQuestion.sectionById =
                            prefillQuestionSectionById;
                        // Update prefill data
                        newPrefillById[questionId] = prefillQuestion;
                        newPrefillQuestionIds = [
                            ...newPrefillQuestionIds,
                            ...prefillQuestionList.map((q) => q.questionId),
                        ];
                    }
                });

                return {
                    prefillQuestionIds: newPrefillQuestionIds,
                    prefillById: newPrefillById,
                };
            }

            // No prefill exists, return empty data
            return { prefillQuestionIds: [], prefillById: {} };
        },
        [],
    );

    // Whenever question list changes, regenerate prefill data
    const { prefillQuestionIds, prefillById } = useMemo(() => {
        if (quizFlow === QUIZ_FLOW_TYPES.renewal) {
            return generatePrefillData(questionList);
        } else {
            return { prefillQuestionIds: [], prefillById: {} };
        }
    }, [questionList, quizFlow, generatePrefillData]);

    return {
        ...(quizResponse !== null ? quizResponse : {}),
        questionList,
        questionById,
        submittedQuestionIds,
        setSubmittedQuestionIds,
        sectionList: quizResponse?.sectionList || [],
        sectionById,
        prefillById,
        prefillQuestionIds,
        medications: quizResponse?.medications || [],
        plans: quizResponse?.plans || [],
        generatePrefillData,
        generateQuestionList,
    };
};

export default useQuiz;
